import { useEffect, useRef } from "react";
import { useGetMyToday, getGetMyTodayQueryKey } from "@workspace/api-client-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth-context";
import { maskMobileNumber } from "@/lib/format";

const STORAGE_KEY = "crm_notified_callback_ids";

function loadNotified(): Set<number> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as number[];
    return new Set(arr);
  } catch {
    return new Set();
  }
}

function persistNotified(set: Set<number>) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // ignore
  }
}

export function CallbackReminder() {
  const { user } = useAuth();
  const { toast } = useToast();
  const notifiedRef = useRef<Set<number>>(loadNotified());

  const { data } = useGetMyToday(
    {},
    {
      query: {
        queryKey: getGetMyTodayQueryKey(),
        enabled: !!user,
        refetchInterval: 30000,
      },
    },
  );

  useEffect(() => {
    if (!user || !data) return;
    const tick = () => {
      const now = Date.now();
      let changed = false;
      for (const lead of data.callbacks) {
        if (!lead.callbackScheduledDate) continue;
        const due = new Date(lead.callbackScheduledDate).getTime();
        if (due <= now && !notifiedRef.current.has(lead.id)) {
          notifiedRef.current.add(lead.id);
          changed = true;
          toast({
            title: "Callback due now",
            description: `${lead.customerName} · ${maskMobileNumber(lead.mobileNumber)}`,
            duration: 15000,
          });
        }
      }
      if (changed) persistNotified(notifiedRef.current);
    };
    tick();
    const handle = window.setInterval(tick, 15000);
    return () => window.clearInterval(handle);
  }, [data, user, toast]);

  return null;
}
